import {
  Appointment,
  QUEUE_CARD_ACTIONS,
  QUEUE_CONFIG_ACTION_ID,
  QUEUE_TYPE,
} from "@elixir/types";
import { Dropdown } from "antd";
import moment from "dayjs";
import React, { useMemo, useState } from "react";
import { ChipWithTooltip, TooltipComponent } from "ui";
import Chip from "../../atoms/chip";
import useQueueStore from "../../stores/queue-store";
import { APPOINTMENT_STATUS } from "../../stores/queue-store/enums";
import { getTimeBasedState } from "../../utils/queue-utils";
import { getQueueTypeFromStatus } from "../../utils/appointment-utils";
import useGetViewActions from "../../hooks/use-get-view-actions";
import CheckInButton from "../../molecules/button/queue/actions/check-in-button";
import { EllipsisRegularIcon } from "@elixir/icons";
import { MainMenu } from "./more-menu";
import QueueCardAction from "../../molecules/chips/queue/queue-card-action";

export const DEFAULT_ACTIONS = [
  "ABHA",
  "ADDR",
  "RMDR",
] as QUEUE_CONFIG_ACTION_ID[];

const RightPanelCardBottomActionButtons = ({
  apt,
  emptyState = false,
}: {
  apt: Appointment;
  emptyState?: boolean;
}) => {
  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  );
  const queueTypeByStatus = getQueueTypeFromStatus(apt.status);
  const viewType = useQueueStore((state) => state.viewType);
  const openLeftPanel = useQueueStore((state) => state.openLeftPanel);

  const [primaryActions, secondaryActions] = useGetViewActions({
    rowIdx: 1,
    apt,
  });

  let actionsConfig = secondaryActions;

  if (viewType === "compact") {
    if (openLeftPanel) {
      actionsConfig = actionsConfig.filter((_, idx) => idx < 1);
    } else {
      actionsConfig = actionsConfig.filter((_, idx) => idx < 0);
    }
  }

  const { visit_checkin, status, visit_start, start_time, visit_end } =
    apt || {};

  const queueType = getQueueTypeFromStatus(status);

  const isOngoing = status === APPOINTMENT_STATUS.ONGOING;

  const [aptTimeStatus, setAptTimeStatus] = useState(() =>
    getTimeBasedState({
      timeToCheck:
        queueType === QUEUE_TYPE.COMPLETED
          ? (visit_start || visit_checkin)!
          : isOngoing
            ? visit_start!
            : visit_checkin!,
      isOngoing: isOngoing,
      currTime:
        queueType === QUEUE_TYPE.COMPLETED ? new Date(visit_end!) : undefined,
    })
  );

  React.useEffect(() => {
    setAptTimeStatus(
      getTimeBasedState({
        timeToCheck:
          queueType === QUEUE_TYPE.COMPLETED
            ? (visit_start || visit_checkin)!
            : isOngoing
              ? visit_start!
              : visit_checkin!,
        isOngoing: isOngoing,
        currTime:
          queueType === QUEUE_TYPE.COMPLETED ? new Date(visit_end!) : undefined,
      })
    );
    const intervalId = setInterval(() => {
      setAptTimeStatus(
        getTimeBasedState({
          timeToCheck:
            queueType === QUEUE_TYPE.COMPLETED
              ? (visit_start || visit_checkin)!
              : isOngoing
                ? visit_start!
                : visit_checkin!,
          isOngoing: isOngoing,
          currTime:
            queueType === QUEUE_TYPE.COMPLETED
              ? new Date(visit_end!)
              : undefined,
        })
      );
    }, 10000);
    return () => clearInterval(intervalId);
  }, [isOngoing, queueType, visit_checkin, visit_end, visit_start]);
  const timePrefix =
    queueType === QUEUE_TYPE.BOOKED
      ? "At: "
      : queueType === QUEUE_TYPE.COMPLETED
        ? "For: "
        : isOngoing
          ? "Since: "
          : "Wait: ";

  const displayTime = useMemo(() => {
    switch (queueType) {
      case QUEUE_TYPE.BOOKED:
        return moment(start_time).format("hh:mm A");
      case QUEUE_TYPE.COMPLETED:
        return `${aptTimeStatus.timeDiffString} | ${moment(visit_end).format(
          "hh:mm A"
        )}`;
      case QUEUE_TYPE.CHECKED_IN: {
        if (isOngoing) {
          return `${aptTimeStatus.timeDiffString} | ${moment(
            visit_start
          ).format("hh:mm A")}`;
        }
        return `${aptTimeStatus.timeDiffString} | ${moment(
          visit_checkin
        ).format("hh:mm A")}`;
      }
    }
    return `${aptTimeStatus.timeDiffString} | ${moment(start_time).format(
      "hh:mm A"
    )}`;
  }, [
    aptTimeStatus.timeDiffString,
    isOngoing,
    queueType,
    start_time,
    visit_checkin,
    visit_end,
    visit_start,
  ]);

  const getActionClassName = (actionId: string) => {
    if (actionId === QUEUE_CARD_ACTIONS.EXIT) return "dr-w-30 dr-h-30";
    if (actionId === QUEUE_CARD_ACTIONS.UPLOAD_MEDICAL_RECORDS)
      return "dr-w-30 dr-h-30";
    return "dr-flex-1 dr-max-w-165";
  };

  return (
    <div
      className={
        viewType === "default"
          ? "dr-w-full dr-flex dr-flex-row dr-space-x-16"
          : ""
      }
    >
      {/* {preference.actions.join(", ")} | {preference.columns.join(", ")} */}
      {viewType === "default" &&
      [QUEUE_TYPE.BOOKED, QUEUE_TYPE.CHECKED_IN, QUEUE_TYPE.COMPLETED].includes(
        queueTypeByStatus
      ) ? (
        <TooltipComponent
          title={
            emptyState
              ? queueType === QUEUE_TYPE.CHECKED_IN
                ? "Wait Time"
                : "Visit Duration"
              : `${timePrefix} ${displayTime}`
          }
          placement="top"
        >
          <div>
            <Chip
              customStyles={{ cursor: "default" }}
              className={`dr-space-x-6 ${aptTimeStatus.borderColor} dr-col-span-1 dr-text-darwin-touch-neutral-1000 dr-flex-1 dr-max-w-165`}
              paddingClassName="dr-px-8 dr-py-6"
              titleClassName="dr-line-clamp-1"
              title={
                emptyState
                  ? queueType === QUEUE_TYPE.CHECKED_IN
                    ? "Wait Time"
                    : "Visit Duration"
                  : `${timePrefix} ${displayTime}`
              }
            />
          </div>
        </TooltipComponent>
      ) : null}
      <div className="dr-flex dr-flex-1 dr-justify-end dr-space-x-16">
        {actionsConfig.map((actionId) => (
          <QueueCardAction
            appointment={apt}
            queueType={rightPanelQueueType}
            columnId={actionId}
            key={actionId}
            className={getActionClassName(actionId)}
          />
        ))}
        <Dropdown
          className="dr-w-30 dr-h-30"
          trigger={["click"]}
          overlay={<MainMenu apt={apt} queueType={queueType} />}
        >
          <ChipWithTooltip
            title={"More"}
            tooltipText="More"
            icon={<EllipsisRegularIcon className="dr-w-12 dr-h-12 dr-rotate-90" />}
            iconOnly={true}
            variant="neutralFilled"
          />
        </Dropdown>
        {/* List of primary actions */}
        {primaryActions.map((columnId) => (
          <React.Fragment key={columnId}>
            <QueueCardAction
              appointment={apt}
              queueType={rightPanelQueueType}
              columnId={columnId}
              type="primary"
              className={getActionClassName(columnId)}
            />
          </React.Fragment>
        ))}
        {rightPanelQueueType === QUEUE_TYPE.BOOKED ? (
          <CheckInButton
            apt={apt}
            type="primary"
            className="dr-flex-1 dr-max-w-165"
          />
        ) : null}
      </div>
    </div>
  );
};

export default RightPanelCardBottomActionButtons;
